import React, { useState, useEffect } from 'react';
import { crmApi } from '../../services/crmApi';

const ClientRevenueTab = ({ client }) => {
  const [revenues, setRevenues] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAddingRevenue, setIsAddingRevenue] = useState(false);
  const [editingRevenue, setEditingRevenue] = useState(null);
  const [formData, setFormData] = useState({
    revenue_month: '',
    amount: '',
    notes: ''
  });

  useEffect(() => {
    if (client?.id) {
      fetchRevenueData();
    }
  }, [client?.id]);

  const fetchRevenueData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const data = await crmApi.getClientRevenue(client.id, { months: 24 });
      setRevenues(data.revenues || []);
      setSummary(data.summary || {});
    } catch (err) {
      console.error('Error fetching revenue data:', err);
      setError('Failed to load revenue data');
    } finally {
      setLoading(false);
    }
  };

  const handleAddRevenue = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      // Validate form
      if (!formData.revenue_month || !formData.amount) {
        setError('Month and amount are required');
        return;
      }

      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount < 0) {
        setError('Amount must be a valid positive number');
        return;
      }

      await crmApi.addRevenue(client.id, {
        revenue_month: formData.revenue_month,
        amount: amount,
        notes: formData.notes
      });

      // Reset form and refresh data
      setFormData({ revenue_month: '', amount: '', notes: '' });
      setIsAddingRevenue(false);
      await fetchRevenueData();
    } catch (err) {
      console.error('Error adding revenue:', err);
      if (err.response?.status === 409) {
        setError('Revenue record already exists for this month. Use edit to update.');
      } else {
        setError(err.response?.data?.error || 'Failed to add revenue record');
      }
    }
  };

  const handleEditRevenue = async (e) => {
    e.preventDefault();
    
    try {
      setError(null);
      
      const amount = parseFloat(formData.amount);
      if (isNaN(amount) || amount < 0) {
        setError('Amount must be a valid positive number');
        return;
      }

      await crmApi.updateRevenue(editingRevenue.id, {
        amount: amount,
        notes: formData.notes
      });

      // Reset form and refresh data
      setFormData({ revenue_month: '', amount: '', notes: '' });
      setEditingRevenue(null);
      await fetchRevenueData();
    } catch (err) {
      console.error('Error updating revenue:', err);
      setError(err.response?.data?.error || 'Failed to update revenue record');
    }
  };

  const handleDeleteRevenue = async (revenueId) => {
    if (!window.confirm('Are you sure you want to delete this revenue record?')) {
      return;
    }

    try {
      setError(null);
      await crmApi.deleteRevenue(revenueId);
      await fetchRevenueData();
    } catch (err) {
      console.error('Error deleting revenue:', err);
      setError(err.response?.data?.error || 'Failed to delete revenue record');
    }
  };

  const startEdit = (revenue) => {
    setEditingRevenue(revenue);
    setFormData({
      revenue_month: revenue.revenue_month?.substring(0, 7) || '', // Extract YYYY-MM for month input
      amount: revenue.amount || '',
      notes: revenue.notes || ''
    });
    setIsAddingRevenue(false);
  };

  const cancelEdit = () => {
    setEditingRevenue(null);
    setIsAddingRevenue(false);
    setFormData({ revenue_month: '', amount: '', notes: '' });
    setError(null);
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  const formatMonth = (dateString) => {
    // dateString is in YYYY-MM-DD format, parse it directly to avoid timezone issues
    const dateParts = dateString.split('-');
    const year = parseInt(dateParts[0]);
    const month = parseInt(dateParts[1]) - 1; // JavaScript months are 0-based
    const date = new Date(year, month, 1); // Create in local timezone
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-32">
        <div className="w-8 h-8 border-t-2 border-b-2 border-indigo-500 rounded-full animate-spin"></div>
        <span className="ml-2 text-gray-600">Loading revenue data...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Revenue Summary */}
      {summary && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Revenue Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-500">Total Revenue</div>
              <div className="text-2xl font-bold text-indigo-600">
                {formatCurrency(summary.total_revenue || 0)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-500">Average Monthly</div>
              <div className="text-2xl font-bold text-green-600">
                {formatCurrency(summary.average_monthly_revenue || 0)}
              </div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-sm text-gray-500">Months with Data</div>
              <div className="text-2xl font-bold text-blue-600">
                {summary.months_with_data || 0}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Revenue Button */}
      <div className="flex justify-between items-center">
        <h3 className="text-lg font-medium text-gray-900">Revenue History</h3>
        {!isAddingRevenue && !editingRevenue && (
          <button
            onClick={() => setIsAddingRevenue(true)}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
          >
            Add Revenue Record
          </button>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="p-4 text-red-700 bg-red-100 rounded-md">
          {error}
        </div>
      )}

      {/* Add/Edit Revenue Form */}
      {(isAddingRevenue || editingRevenue) && (
        <div className="bg-gray-50 p-6 rounded-lg">
          <h4 className="text-md font-medium text-gray-900 mb-4">
            {editingRevenue ? 'Edit Revenue Record' : 'Add Revenue Record'}
          </h4>
          <form onSubmit={editingRevenue ? handleEditRevenue : handleAddRevenue}>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Month *
                </label>
                <input
                  type="month"
                  value={formData.revenue_month}
                  onChange={(e) => setFormData(prev => ({ ...prev, revenue_month: e.target.value }))}
                  disabled={!!editingRevenue}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 disabled:bg-gray-100"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Amount * ($)
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.amount}
                  onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Notes
                </label>
                <input
                  type="text"
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
                  placeholder="Optional notes"
                />
              </div>
            </div>
            <div className="mt-4 flex space-x-3">
              <button
                type="submit"
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                {editingRevenue ? 'Update' : 'Add'} Revenue
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md shadow-sm text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Revenue Records Table */}
      {revenues.length > 0 ? (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {revenues.map((revenue) => (
              <li key={revenue.id} className="px-6 py-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-gray-900">
                          {formatMonth(revenue.revenue_month)}
                        </p>
                        <p className="text-lg font-bold text-indigo-600">
                          {formatCurrency(revenue.amount)}
                        </p>
                        {revenue.notes && (
                          <p className="text-sm text-gray-500 mt-1">
                            {revenue.notes}
                          </p>
                        )}
                      </div>
                      <div className="text-right text-sm text-gray-500">
                        <p>Added {new Date(revenue.created_at).toLocaleDateString()}</p>
                        {revenue.updated_at !== revenue.created_at && (
                          <p>Updated {new Date(revenue.updated_at).toLocaleDateString()}</p>
                        )}
                      </div>
                    </div>
                  </div>
                  <div className="ml-4 flex space-x-2">
                    <button
                      onClick={() => startEdit(revenue)}
                      className="text-indigo-600 hover:text-indigo-900 text-sm font-medium"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteRevenue(revenue.id)}
                      className="text-red-600 hover:text-red-900 text-sm font-medium"
                    >
                      Delete
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      ) : (
        <div className="text-center py-12">
          <div className="text-gray-500">
            <svg
              className="mx-auto h-12 w-12 text-gray-400"
              stroke="currentColor"
              fill="none"
              viewBox="0 0 48 48"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
              />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No revenue records</h3>
            <p className="mt-1 text-sm text-gray-500">
              Get started by adding a revenue record for this client.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientRevenueTab;
